import { useState, type FormEvent } from "react"
import { useMutation } from "@tanstack/react-query"
import { Link, useNavigate } from "react-router-dom"
import { apiRequest } from "../lib/api"
import type { ApiError } from "../lib/api"
import type { User } from "../types"

type AuthResponse = {
  user: User
}

type SignInProps = {
  onAuth: (user: User) => void
}

const SignIn = ({ onAuth }: SignInProps) => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const signInMutation = useMutation<AuthResponse, ApiError>({
    mutationFn: () =>
      apiRequest<AuthResponse>("/api/sign_in", {
        method: "POST",
        body: JSON.stringify({ user: { email, password } }),
      }),
    onSuccess: (data) => {
      onAuth(data.user)
      navigate("/", { replace: true })
    },
    onError: (err) => {
      setError(err.message ?? "Sign in failed")
    },
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    signInMutation.mutate()
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10 text-foreground">
      <div className="w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-xs">
        <div className="space-y-1 p-6">
          <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
          <p className="text-sm text-muted-foreground">
            Welcome back. Enter your credentials.
          </p>
        </div>
        <div className="space-y-4 p-6 pt-0">
          {error ? (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <button
              type="submit"
              disabled={signInMutation.isPending}
              className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:opacity-90 disabled:opacity-60"
            >
              {signInMutation.isPending ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
        <div className="p-6 pt-0 text-sm text-muted-foreground">
          <span>New here?</span>{" "}
          <Link
            to="/sign-up"
            className="text-primary underline-offset-4 hover:underline"
          >
            Create an account
          </Link>
        </div>
      </div>
    </div>
  )
}

export default SignIn
